import React, { FC, useState, useEffect } from 'react';

import getConfig from 'next/config';

import { ButtonIconLeft, DarkTooltip, Icon, Text } from '@morressier/ts';

import { useMediaQuery } from '@material-ui/core';
import axios from 'axios';
import { FaCloudDownloadAlt } from 'react-icons/fa';

import { FeatureFlags } from 'config/featureFlags';
import { useAuthedUser } from 'hooks/useIsAuthedUser';
import { useRegAndTicketingContext } from 'store/modules/eventTicketing';
import { logger } from 'utils/logger';

import * as Styles from './styles';

export const PROCESSING_STATE_QUEUED = 0;
export const PROCESSING_STATE_PROCESSING = 1;
export const PROCESSING_STATE_DONE = 2;

export const BREAKPOINT_MEDIUM = 721;

const { publicRuntimeConfig } = getConfig();

const PresentationSection: FC<{
  poster: MorressierPoster;
  pdfUrl: string;
  standaloneEvent?: string;
}> = props => {
  const { poster, pdfUrl } = props;
  const isDesktop = useMediaQuery(`(min-width:${BREAKPOINT_MEDIUM}px)`);
  const [isPdf, setIsPdf] = useState<boolean | null>(null);

  const { processAction, state } = useRegAndTicketingContext();
  const isAuthed = useAuthedUser();
  const isTicketingFlagEnabled = FeatureFlags.Event_Ticketing.isEnabled();

  const disableDownload = state.isPrivateEvent
    ? isTicketingFlagEnabled && !state.userHasAccess
    : !isAuthed;

  useEffect(() => {
    const testPdf = async () => {
      try {
        await axios.get(pdfUrl);
        setIsPdf(true);
      } catch (err) {
        logger.log('testPdf.err:', err);
        setIsPdf(false);
      }
    };

    testPdf();
  }, [pdfUrl]);

  const zeroState = (
    <Styles.ZeroStateBox>
      <Styles.ZeroStateBoxWrapper>
        <Styles.GrayBox />
        <Styles.TextWrapper>
          <Text color="grey" style={{ textAlign: 'center' }}>
            The authors are in the process of completing this submission.
          </Text>
          <Text color="grey">Please try again later.</Text>
        </Styles.TextWrapper>
      </Styles.ZeroStateBoxWrapper>
    </Styles.ZeroStateBox>
  );

  if (poster.processing_state === PROCESSING_STATE_QUEUED) {
    return zeroState;
  }

  const pdfReady = !!pdfUrl && poster.processing_state === PROCESSING_STATE_DONE;

  // show thumbnail if pdfUrl doesn't exist or file is not yet processed,
  // happens when user access this page unauthenticated
  if (!pdfUrl || poster.processing_state !== PROCESSING_STATE_DONE || !isDesktop) {
    return (
      <>
        <Styles.Thumbnail
          alt="thumbnail"
          src={poster.thumb_url_large || poster.thumb_url}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null; // prevents looping
            currentTarget.src =
              'https://thesummit-production.s3.eu-central-1.amazonaws.com/default/nopreview.png';
          }}
        />
        <ButtonIconLeft
          style={{
            marginTop: '1rem',
            borderColor: 'lightgray',
          }}
          width="100%"
          onClick={() => {
            if (disableDownload) {
              processAction(() => null);
            } else if (pdfReady) {
              window.open(pdfUrl, '_blank');
            }
          }}
          disabled={!pdfReady}
          onContextMenu={e => e.preventDefault()}
        >
          <Icon
            style={{
              position: 'static',
              marginRight: '0.5rem',
              verticalAlign: 'middle',
            }}
            as={FaCloudDownloadAlt}
          />
          Download
        </ButtonIconLeft>
      </>
    );
  }

  return (
    <Styles.PdfWrapper disableDownload={disableDownload}>
      <object
        data={`${publicRuntimeConfig.api_base}/api/v1/discovery/files/${encodeURIComponent(
          props.pdfUrl ?? '',
        )}?file_name=${poster.title}#toolbar=1`}
        type="application/pdf"
        style={{
          width: '100%',
          height: '100%',
        }}
        aria-label="Embedded PDF"
      />
      {disableDownload && (
        <>
          {/* <div className="block-download" onContextMenu={e => e.preventDefault()} /> */}
          <div
            role="presentation"
            onClick={() => {
              processAction(() => null);
            }}
            className="block-buttons"
            onContextMenu={e => e.preventDefault()}
          >
            <DarkTooltip
              placement="top"
              shift="-3.5rem"
              width="10rem"
              text="Sign in to download this content"
            />
          </div>
        </>
      )}
    </Styles.PdfWrapper>
  );
};

export default PresentationSection;
